import {
  type EnhancedMenu,
  type EnhancedMenuItem,
  useIsHomePath,
} from '~/lib/utils';
import {
  Drawer,
  useDrawer,
  Text,
  Input,
  IconLogin,
  IconAccount,
  IconBag,
  IconSearch,
  Heading,
  IconMenu,
  IconCaret,
  Section,
  CountrySelector,
  Cart,
  CartLoading,
  Link,
} from '~/components';
import {useParams, Form, Await, useMatches} from '@remix-run/react';
import {useWindowScroll} from 'react-use';
import {Disclosure} from '@headlessui/react';
import {Suspense, useEffect, useMemo, useRef} from 'react';
import {useIsHydrated} from '~/hooks/useIsHydrated';
import {useCartFetchers} from '~/hooks/useCartFetchers';
import type {LayoutData} from '../root';
import clsx from 'clsx';
import {loader} from '../root';

//components
import Header from 'components/global/Header';
import Footer from 'components/global/Footer';
import EmailSignUp from 'components/global/EmailSignUp';
import Sidebar from 'components/global/Sidebar';

//domains
import type {Announcement} from '~/domains/modules';

export function Layout({
  children,
  layout,
}: {
  children: React.ReactNode;
  layout: LayoutData;
}) {
  const [root] = useMatches();
  const announcement = root?.data.announcement as Announcement;
  const isHydrated = useIsHydrated();

  // The header is position:fixed, so <main> must be offset by its height.
  // The pt-header* tokens below are only an SSR approximation — the real
  // height changes when text wraps (browser zoom, narrow windows, longer
  // announcement copy), which let the header overlap the top of the page
  // (e.g. the PLP category bar). Measure the real height and keep the
  // offset in sync.
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const header = document.querySelector<HTMLElement>('header[role="banner"]');
    const main = mainRef.current;
    if (!header || !main) return;
    const apply = () => {
      // The mobile search bar is position:fixed, so it is NOT part of
      // header.offsetHeight. Add its height back or the fixed white bar
      // overlaps the top of the first module (the hero banner). On desktop
      // it is in-flow (md:relative) and already counted, so this adds 0.
      const searchBar = Array.from(
        document.querySelectorAll<HTMLElement>('[data-mobile-search-bar]'),
      ).find(
        (el) => getComputedStyle(el).position === 'fixed' && el.offsetHeight > 0,
      );
      main.style.paddingTop = `${
        header.offsetHeight + (searchBar?.offsetHeight ?? 0)
      }px`;
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(header);
    document
      .querySelectorAll<HTMLElement>('[data-mobile-search-bar]')
      .forEach((el) => ro.observe(el));
    window.addEventListener('resize', apply);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, []);

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <div className="">
          <a href="#mainContent" className="sr-only">
            Skip to content
          </a>
        </div>
        <Header
          title={layout?.shop.name ?? 'Hydrogen'}
          menu={layout?.headerMenu}
        />
        <main
          ref={mainRef}
          role="main"
          id="mainContent"
          className={clsx(
            'flex-grow bg-white text-black',
            announcement.enabled
              ? 'lg:pt-headerDesktop pt-headerMobile'
              : 'lg:pt-headerDesktopWithoutAnnouncement pt-headerMobileWithoutAnnouncement',
          )}
        >
          {children}
        </main>
        <Sidebar />
      </div>
      {/* <Footer menu={layout?.footerMenu} /> */}
      {/* {isHydrated && <EmailSignUp />} */}
      <EmailSignUp />
      <Footer />
    </>
  );
}

export function MenuDrawer({
  isOpen,
  onClose,
  menu,
}: {
  isOpen: boolean;
  onClose: () => void;
  menu: EnhancedMenu;
}) {
  return (
    <Drawer open={isOpen} onClose={onClose} openFrom="left" heading="Menu">
      <div className="grid">
        <MenuMobileNav menu={menu} onClose={onClose} />
      </div>
    </Drawer>
  );
}

function MenuMobileNav({
  menu,
  onClose,
}: {
  menu: EnhancedMenu;
  onClose: () => void;
}) {
  return (
    <nav className="grid gap-4 p-6 sm:gap-6 sm:px-12 sm:py-8">
      {/* Top level menu items */}
      {(menu?.items || []).map((item) => (
        <span key={item.id} className="block">
          <Link
            to={item.to}
            target={item.target}
            onClick={onClose}
            className={({isActive}) =>
              isActive ? 'pb-1 border-b -mb-px' : 'pb-1'
            }
          >
            <Text as="span" size="copy">
              {item.title}
            </Text>
          </Link>
        </span>
      ))}
    </nav>
  );
}

// function Footer({menu}: {menu?: EnhancedMenu}) {
//   const isHome = useIsHomePath();
//   const itemsCount = menu
//     ? menu?.items?.length + 1 > 4
//       ? 4
//       : menu?.items?.length + 1
//     : [];

//   return (
//     <Section
//       divider={isHome ? 'none' : 'top'}
//       as="footer"
//       role="contentinfo"
//       className={`grid min-h-[25rem] items-start grid-flow-row w-full gap-6 py-8 px-6 md:px-8 lg:px-12 md:gap-8 lg:gap-12 grid-cols-1 md:grid-cols-2 lg:grid-cols-${itemsCount}
//         bg-primary dark:bg-contrast dark:text-primary text-contrast overflow-hidden`}
//     >
//       <FooterMenu menu={menu} />
//       <CountrySelector />
//       <div
//         className={`self-end pt-8 opacity-50 md:col-span-2 lg:col-span-${itemsCount}`}
//       >
//         &copy; {new Date().getFullYear()} / Shopify, Inc. Hydrogen is an MIT
//         Licensed Open Source project.
//       </div>
//     </Section>
//   );
// }

const FooterLink = ({item}: {item: EnhancedMenuItem}) => {
  if (item.to.startsWith('http')) {
    return (
      <a href={item.to} target={item.target} rel="noopener noreferrer">
        {item.title}
      </a>
    );
  }

  return (
    <Link to={item.to} target={item.target} prefetch="intent">
      {item.title}
    </Link>
  );
};

function FooterMenu({menu}: {menu?: EnhancedMenu}) {
  const styles = {
    section: 'grid gap-4',
    nav: 'grid gap-2 pb-6',
  };

  return (
    <>
      {(menu?.items || []).map((item: EnhancedMenuItem) => (
        <section key={item.id} className={styles.section}>
          <Disclosure>
            {({open}) => (
              <>
                <Disclosure.Button className="text-left md:cursor-default">
                  <Heading className="flex justify-between" size="lead" as="h3">
                    {item.title}
                    {item?.items?.length > 0 && (
                      <span className="md:hidden">
                        <IconCaret direction={open ? 'up' : 'down'} />
                      </span>
                    )}
                  </Heading>
                </Disclosure.Button>
                {item?.items?.length > 0 ? (
                  <div
                    className={`${
                      open ? `max-h-48 h-fit` : `max-h-0 md:max-h-fit`
                    } overflow-hidden transition-all duration-300`}
                  >
                    <Suspense data-comment="This suspense fixes a hydration bug in Disclosure.Panel with static prop">
                      <Disclosure.Panel static>
                        <nav className={styles.nav}>
                          {item.items.map((subItem) => (
                            <FooterLink key={subItem.id} item={subItem} />
                          ))}
                        </nav>
                      </Disclosure.Panel>
                    </Suspense>
                  </div>
                ) : null}
              </>
            )}
          </Disclosure>
        </section>
      ))}
    </>
  );
}
